import React, { forwardRef, ForwardRefRenderFunction } from "react";
//#region types
import { DeviationFormProps } from "models/propTypes/salaryDeviationPropTypes";
import {
  AutoCompleteExtraProps,
  DateFormFieldProps,
  EditableTableTabFieldProps,
  FormFieldDefinitionType,
  FormHandles,
  NumberFormatFieldExtraProps,
} from "models/propTypes/genericFormProps";
import { FormEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { SalaryDeviationFormTypes } from "models/formTypes";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { GenericForm } from "components/common";
import { useCompanyService } from "serviceHooks";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const CreateLeaveForm: ForwardRefRenderFunction<FormHandles, DeviationFormProps> = (props, ref) => {
  const companyService = useCompanyService();
  const {
    isSalaryAdmin,
    isBusinessUnitManagerWithSalaryRights,
    isDeviationAdmin,
    datesChangeHandler,
    allowedDeviationTypes,
    yearMonth,
    isNew,
  } = props;

  const initialFocusedYearMonth = React.useMemo(() => {
    return yearMonth !== undefined ? DateHelper.getDateFromYearMonth(yearMonth) : undefined;
  }, [yearMonth]);

  const form = React.useMemo(
    () => (
      <GenericForm<SalaryDeviationFormTypes.CreateLeave>
        formRef={ref}
        {...props}
        fields={{
          deviationType: {
            controlLabel: TranslationKeys.SalaryDeviation.TypeOfDeviation,
            label: "",
            type: FormEnums.FormFieldType.CHOICE,
            extraProps: {
              items: allowedDeviationTypes.map((type) => ({
                id: type,
                value: LocalizationHelperInstance.getFormattedMessage(`SalaryDeviation.Type_${type}`),
              })),
            },
            disabled: props.disabled,
          },
          personName: {
            controlLabel: " ",
            label: TranslationKeys.Common.Employee,
            type: FormEnums.FormFieldType.AUTOCOMPLETE,
            disabled:
              !(isSalaryAdmin || isBusinessUnitManagerWithSalaryRights || isDeviationAdmin) || props.disabled || !isNew,
            icon: IconDefinitionConstants.WUser,
            extraProps: {
              items: (searchStr: string) =>
                companyService.SearchPersonsAutoComplete(
                  searchStr,
                  false,
                  true,
                  isBusinessUnitManagerWithSalaryRights && !isSalaryAdmin
                ),
              referenceFieldName: FormFieldConstants.SalaryDeviation.CreateLeave.PersonId,
            } as AutoCompleteExtraProps<SalaryDeviationFormTypes.CreateLeave>,
          },
          customName: {
            label: TranslationKeys.SalaryDeviation.NameOfDeviation,
            type: FormEnums.FormFieldType.TEXT,
            hidden: (values: SalaryDeviationFormTypes.CreateAllowance) =>
              values.deviationType !== WintSuperkollAPIModel.SalaryDeviationType.CustomTimeDeviation,
            disabled: props.disabled,
            descriptionText: TranslationKeys.Allowance.NameOfDeviationDescription,
          },
          childName: {
            controlLabel: " ",
            label: TranslationKeys.SalaryDeviation.Children,
            descriptionText: TranslationKeys.SalaryDeviation.ChildrenHelpText,
            icon: IconDefinitionConstants.WChild,
            type: FormEnums.FormFieldType.TEXT,
            hidden: (values: SalaryDeviationFormTypes.CreateLeave) =>
              ![
                WintSuperkollAPIModel.SalaryDeviationType.ParentalLeave,
                WintSuperkollAPIModel.SalaryDeviationType.TenDays,
              ].some((t) => t === values.deviationType),
            disabled: props.disabled,
          },
          fromDate: {
            label: "",
            type: FormEnums.FormFieldType.HORIONTALFIELDGROUP,
            disabled: props.disabled,
            innerFields: {
              fromDate: {
                label: TranslationKeys.Common.DateFrom,
                onChangeHandler: datesChangeHandler,
                type: FormEnums.FormFieldType.DATE,
                widthColumnCount: 6,
                extraProps: {
                  maxDateRefFieldName: FormFieldConstants.SalaryDeviation.CreateLeave.ToDate,
                  initialFocusedDate: initialFocusedYearMonth,
                } as DateFormFieldProps<SalaryDeviationFormTypes.CreateLeave>,
                disabled: props.disabled,
              },
              toDate: {
                label: TranslationKeys.Common.DateTo,
                onChangeHandler: datesChangeHandler,
                type: FormEnums.FormFieldType.DATE,
                widthColumnCount: 6,
                extraProps: {
                  minDateRefFieldName: FormFieldConstants.SalaryDeviation.CreateLeave.FromDate,
                  initialFocusedDate: initialFocusedYearMonth,
                } as DateFormFieldProps<SalaryDeviationFormTypes.CreateLeave>,
                disabled: props.disabled,
              },
            },
          },
          fillDayHours: {
            label: TranslationKeys.SalaryDeviation.FillHours,
            type: FormEnums.FormFieldType.TOGGLE,
            classes: { formControl: cssExports.inlineLabel },
            disabled: props.disabled,
            onChangeHandler: datesChangeHandler,
            hidden: (values: SalaryDeviationFormTypes.CreateLeave) =>
              [
                WintSuperkollAPIModel.SalaryDeviationType.Vacation,
                WintSuperkollAPIModel.SalaryDeviationType.TenDays,
                WintSuperkollAPIModel.SalaryDeviationType.PaidShortTermLeave,
              ].some((t) => t === values.deviationType),
          },
          dayHours: {
            label: "",
            controlLabel: TranslationKeys.SalaryDeviation.DeviationsDays,
            type: FormEnums.FormFieldType.EDITABLETABLE,
            disabled: props.disabled,
            hidden: (values: SalaryDeviationFormTypes.CreateLeave) =>
              !values.fillDayHours ||
              [
                WintSuperkollAPIModel.SalaryDeviationType.Vacation,
                WintSuperkollAPIModel.SalaryDeviationType.TenDays,
                WintSuperkollAPIModel.SalaryDeviationType.PaidShortTermLeave,
              ].some((t) => t === values.deviationType),
            innerFields: {
              day: {
                label: TranslationKeys.Common.DayPascal,
                placeholder: TranslationKeys.Common.DayPascal,
                type: FormEnums.FormFieldType.DATE,
                disabled: props.disabled,
                extraProps: {
                  minDateRefFieldName: FormFieldConstants.SalaryDeviation.CreateLeave.FromDate,
                  maxDateRefFieldName: FormFieldConstants.SalaryDeviation.CreateLeave.ToDate,
                } as DateFormFieldProps<SalaryDeviationFormTypes.CreateLeave>,
              },
              hours: {
                label: TranslationKeys.Common.HoursPascal,
                type: FormEnums.FormFieldType.NUMBER,
                widthColumnCount: 3,
                disabled: props.disabled,
                extraProps: {
                  max: 8,
                  allowNegative: false,
                } as NumberFormatFieldExtraProps,
              },
              minutes: {
                label: TranslationKeys.Common.MinutesPascal,
                type: FormEnums.FormFieldType.NUMBER,
                widthColumnCount: 3,
                disabled: props.disabled,
                extraProps: {
                  max: 59,
                  allowNegative: false,
                } as NumberFormatFieldExtraProps,
              },
            } as FormFieldDefinitionType<SalaryDeviationFormTypes.HoursByDay>,
            extraProps: {
              idFieldKey: FormFieldConstants.SalaryDeviation.HoursByDay.Id,
              hideActions: true,
            } as EditableTableTabFieldProps<SalaryDeviationFormTypes.HoursByDay>,
          },
          comment: {
            controlLabel: " ",
            label: TranslationKeys.Common.Comment,
            type: FormEnums.FormFieldType.TEXTAREA,
            icon: IconDefinitionConstants.WCommentDots,
            disabled: props.disabled,
            descriptionText: TranslationKeys.Allowance.CommentDescription,
          },
        }}
        hideSuccess
        formGridLayout={{ message: false }}
      />
    ),
    [props.defaultValues]
  );

  return form;
};

export default forwardRef(CreateLeaveForm);
